import _ from 'lodash';

/**
 * Scales the dimensions and elements of a print template so that it fits within a specified maximum width and height,
 * maintaining the aspect ratio of the template.
 *
 * @param {Object} templateData - The template data to be scaled, containing width, height, and elements.
 * @param {number} maxWidth - The maximum width the scaled template should fit within.
 * @param {number} maxHeight - The maximum height the scaled template should fit within.
 * @returns {Object} The scaled template data.
 * @throws {Error} If the template data is invalid or missing essential properties.
 */
export const scaleTemplateToFit = (templateData, maxWidth, maxHeight) => {
  if (!templateData || !templateData.width || !templateData.height) {
    throw new Error('Invalid template data');
  }
  if (!maxWidth || !maxHeight) {
    throw new Error('Invalid max size values');
  }

  // Determine the scale factor based on the maximum dimensions
  const widthRatio = maxWidth / templateData.width;
  const heightRatio = maxHeight / templateData.height;
  const scaleFactor = Math.min(widthRatio, heightRatio);

  const transformedTemplate = _.cloneDeep(templateData);
  // Scale template's width and height
  transformedTemplate.width *= scaleFactor;
  transformedTemplate.height *= scaleFactor;

  // Properties to scale within each element
  const scalableProps = ['xOrigin', 'yOrigin', 'width', 'height', 'size'];

  // Scale elements of the template
  ['images', 'texts', 'placeholders'].forEach((elementType) => {
    const elements = transformedTemplate[elementType];
    if (!elements || !elements.length) {
      return;
    }

    transformedTemplate[elementType] = elements.map((element) => {
      scalableProps.forEach((prop) => {
        if (element[prop] !== undefined) {
          element[prop] *= scaleFactor;
        }
      });
      return element;
    });
  });

  return transformedTemplate;
};
